import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, FileText, ListChecks, LogOut, Plus, Trash2,
  Search, Loader2, X, Upload, Eye, Download, Mail, Settings,
} from "lucide-react";
import { adminApi, API } from "@/lib/api";
import SettingsPanel from "@/pages/SettingsPanel";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [afms, setAfms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [newAfm, setNewAfm] = useState("");
  const [newNote, setNewNote] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!localStorage.getItem("hireflow_admin_token")) {
      nav("/admin");
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [a, s] = await Promise.all([
        adminApi.get("/admin/afm"),
        adminApi.get("/admin/submissions"),
      ]);
      setAfms(a.data.items || []);
      setSubmissions(s.data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("hireflow_admin_token");
        nav("/admin");
        return;
      }
      setError(err?.response?.data?.detail || "Σφάλμα φόρτωσης");
    } finally {
      setLoading(false);
    }
  }, [nav]);

  async function addAfm(e) {
    e.preventDefault();
    if (!newAfm.trim()) return;
    try {
      await adminApi.post("/admin/afm", { afm: newAfm.trim(), note: newNote.trim() || null });
      setNewAfm(""); setNewNote("");
      refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Σφάλμα προσθήκης");
    }
  }

  async function bulkAdd() {
    const list = bulkText.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) return;
    try {
      const res = await adminApi.post("/admin/afm/bulk", { afms: list });
      setBulkText("");
      setBulkOpen(false);
      alert(`Προστέθηκαν ${res.data.added} ΑΦΜ. Παραλείφθηκαν ${res.data.skipped} (διπλότυπα).`);
      refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Σφάλμα bulk");
    }
  }

  async function delAfm(afm) {
    if (!window.confirm(`Διαγραφή ΑΦΜ ${afm};`)) return;
    try {
      await adminApi.delete(`/admin/afm/${afm}`);
      refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Σφάλμα διαγραφής");
    }
  }

  function logout() {
    localStorage.removeItem("hireflow_admin_token");
    nav("/admin");
  }

  function viewPdf(id) {
    const token = localStorage.getItem("hireflow_admin_token");
    // Use fetch to download with auth header, then open as blob
    fetch(`${API}/admin/submissions/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => window.open(URL.createObjectURL(b), "_blank"));
  }

  function downloadResume(id, filename, index = null) {
    const token = localStorage.getItem("hireflow_admin_token");
    const url = index !== null
      ? `${API}/admin/submissions/${id}/resume/${index}`
      : `${API}/admin/submissions/${id}/resume`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const blobUrl = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = blobUrl; a.download = filename || "resume"; a.click();
        URL.revokeObjectURL(blobUrl);
      });
  }

  const filteredAfms = afms.filter((a) =>
    !search || a.afm.includes(search) || (a.note || "").toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    afms: afms.length,
    total: submissions.length,
    sent: submissions.filter((s) => s.status === "sent").length,
    draft: submissions.filter((s) => s.status === "draft").length,
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-slate-200 flex flex-col p-5 sticky top-0 h-screen">
        <div className="flex items-center gap-2 text-blue-300 text-xs font-bold tracking-[0.2em] uppercase mb-8">
          <span className="inline-block w-6 h-px bg-blue-300"></span>
          HireFlow Admin
        </div>
        <nav className="flex-1 space-y-1">
          <SideBtn id="overview" current={tab} setTab={setTab} icon={<ListChecks size={18} />}>Επισκόπηση</SideBtn>
          <SideBtn id="afms" current={tab} setTab={setTab} icon={<Building2 size={18} />}>Λίστα ΑΦΜ</SideBtn>
          <SideBtn id="submissions" current={tab} setTab={setTab} icon={<FileText size={18} />}>Υποβολές</SideBtn>
          <SideBtn id="settings" current={tab} setTab={setTab} icon={<Settings size={18} />}>Ρυθμίσεις</SideBtn>
        </nav>
        <button
          onClick={logout}
          className="flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-white px-3 py-2 rounded-lg transition"
          data-testid="admin-logout-btn"
        >
          <LogOut size={16} /> Αποσύνδεση
        </button>
      </aside>

      <main className="flex-1 p-6 md:p-10 overflow-auto">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-sm text-red-700 font-semibold">
            {error}
          </div>
        )}

        {tab === "overview" && (
          <div className="hf-fade-up">
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mb-2">Επισκόπηση</h1>
            <p className="text-slate-500 font-medium mb-8">Συνοπτική εικόνα του συστήματος.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 hf-stagger">
              <StatCard label="Έγκυρα ΑΦΜ" value={stats.afms} icon={<Building2 size={20} />} color="bg-blue-50 text-blue-700" testId="stat-afms" />
              <StatCard label="Σύνολο Υποβολών" value={stats.total} icon={<FileText size={20} />} color="bg-violet-50 text-violet-700" testId="stat-total" />
              <StatCard label="Απεσταλμένες" value={stats.sent} icon={<Mail size={20} />} color="bg-emerald-50 text-emerald-700" testId="stat-sent" />
              <StatCard label="Σε Προεπισκόπηση" value={stats.draft} icon={<Eye size={20} />} color="bg-amber-50 text-amber-700" testId="stat-draft" />
            </div>
          </div>
        )}

        {tab === "afms" && (
          <div className="hf-fade-up">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Λίστα ΑΦΜ</h1>
                <p className="text-slate-500 font-medium mt-2">
                  Διαχείριση των αποδεκτών ΑΦΜ Επιχειρήσεων που εμφανίζονται στη φόρμα.
                </p>
              </div>
              <button
                onClick={() => setBulkOpen(true)}
                className="h-12 px-5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-xl flex items-center gap-2 transition"
                data-testid="bulk-import-btn"
              >
                <Upload size={16} /> Μαζική Εισαγωγή
              </button>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Προσθήκη νέου ΑΦΜ</h3>
              <form onSubmit={addAfm} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3">
                <input
                  data-testid="new-afm-input"
                  placeholder="ΑΦΜ"
                  className="hf-input"
                  value={newAfm}
                  onChange={(e) => setNewAfm(e.target.value)}
                  inputMode="numeric"
                  maxLength={9}
                />
                <input
                  data-testid="new-afm-note-input"
                  placeholder="Σημείωση (προαιρετικό) — π.χ. όνομα εταιρείας"
                  className="hf-input"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                />
                <button type="submit" className="h-12 px-5 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-xl flex items-center gap-2" data-testid="add-afm-btn">
                  <Plus size={18} /> Προσθήκη
                </button>
              </form>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3">
                <Search size={16} className="text-slate-400" />
                <input
                  placeholder="Αναζήτηση…"
                  className="flex-1 outline-none text-sm font-medium bg-transparent"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="afm-search-input"
                />
                <span className="text-xs text-slate-500 font-semibold">{filteredAfms.length} εγγραφές</span>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">ΑΦΜ</th>
                    <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Σημείωση</th>
                    <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Δημιουργία</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-400"><Loader2 className="animate-spin inline" size={18} /></td></tr>}
                  {!loading && filteredAfms.length === 0 && (
                    <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-400 font-medium">Δεν υπάρχουν ΑΦΜ.</td></tr>
                  )}
                  {filteredAfms.map((a) => (
                    <tr key={a.afm} className="border-t border-slate-100 hover:bg-slate-50/60">
                      <td className="px-5 py-3 font-mono text-sm font-bold text-slate-900">{a.afm}</td>
                      <td className="px-5 py-3 text-sm text-slate-600">{a.note || "—"}</td>
                      <td className="px-5 py-3 text-xs text-slate-500">{a.created_at?.slice(0, 10)}</td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => delAfm(a.afm)} className="text-red-600 hover:bg-red-50 rounded-lg p-2 transition" data-testid={`delete-afm-${a.afm}`}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "submissions" && (
          <div className="hf-fade-up">
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Υποβολές</h1>
            <p className="text-slate-500 font-medium mt-2 mb-8">Όλες οι φόρμες πρόσληψης που έχουν δημιουργηθεί.</p>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Ημ/νία</th>
                    <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Εργαζόμενος</th>
                    <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Επιχείρηση</th>
                    <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Επικοινωνία</th>
                    <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Κατάσταση</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400"><Loader2 className="animate-spin inline" size={18} /></td></tr>}
                  {!loading && submissions.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400 font-medium">Δεν υπάρχουν υποβολές.</td></tr>
                  )}
                  {submissions.map((s) => (
                    <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                      <td className="px-5 py-3 text-xs text-slate-500">{(s.submitted_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-5 py-3 text-sm font-bold text-slate-900">{s.last_name} {s.first_name}</td>
                      <td className="px-5 py-3 text-sm text-slate-700">{s.company_name} <span className="text-slate-400 font-mono text-xs">({s.company_afm})</span></td>
                      <td className="px-5 py-3 text-sm text-slate-600">{s.phone_mobile}{s.email ? ` · ${s.email}` : ""}</td>
                      <td className="px-5 py-3">
                        <StatusPill status={s.status} />
                      </td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <button onClick={() => viewPdf(s.id)} className="inline-flex items-center gap-1 text-blue-700 hover:bg-blue-50 rounded-lg px-2 py-1 text-sm font-bold mr-1" data-testid={`view-pdf-${s.id}`}>
                          <Eye size={14} /> PDF
                        </button>
                        {(s.files_count > 0 || s.resume_filename) && (
                          <FilesDropdown
                            files={s.files_names && s.files_names.length ? s.files_names : (s.resume_filename ? [s.resume_filename] : [])}
                            onDownload={(idx, name) => downloadResume(s.id, name, s.files_names && s.files_names.length ? idx : null)}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {tab === "settings" && <SettingsPanel />}
      </main>

      {/* Bulk modal */}
      {bulkOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={() => setBulkOpen(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-extrabold tracking-tight">Μαζική Εισαγωγή ΑΦΜ</h3>
              <button onClick={() => setBulkOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <p className="text-sm text-slate-500 font-medium mb-3">
              Επικολλήστε ΑΦΜ χωρισμένα με κενό, κόμμα, ή νέα γραμμή.
            </p>
            <textarea
              data-testid="bulk-textarea"
              className="hf-input hf-textarea w-full"
              rows={8}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder="123456789&#10;987654321&#10;..."
            />
            <div className="flex gap-3 mt-4 justify-end">
              <button onClick={() => setBulkOpen(false)} className="h-11 px-5 border border-slate-200 hover:bg-slate-50 rounded-xl font-bold text-slate-700">Ακύρωση</button>
              <button onClick={bulkAdd} className="h-11 px-6 bg-blue-700 hover:bg-blue-800 text-white rounded-xl font-bold" data-testid="bulk-submit-btn">Εισαγωγή</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilesDropdown({ files, onDownload }) {
  const [open, setOpen] = React.useState(false);
  if (!files || files.length === 0) return null;
  // Single file: just a single button
  if (files.length === 1) {
    return (
      <button
        onClick={() => onDownload(0, files[0])}
        className="inline-flex items-center gap-1 text-slate-700 hover:bg-slate-100 rounded-lg px-2 py-1 text-sm font-bold"
      >
        <Download size={14} /> Αρχείο
      </button>
    );
  }
  // Multiple files: dropdown
  return (
    <div className="inline-block relative">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-slate-700 hover:bg-slate-100 rounded-lg px-2 py-1 text-sm font-bold"
      >
        <Download size={14} /> {files.length} αρχεία
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-20 min-w-[220px] max-w-[320px] p-1">
            {files.map((name, idx) => (
              <button
                key={`${name}-${idx}`}
                onClick={() => { onDownload(idx, name); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2 truncate"
                title={name}
              >
                <Download size={12} className="shrink-0 text-blue-700" />
                <span className="truncate">{name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SideBtn({ id, current, setTab, icon, children }) {
  const active = current === id;
  return (
    <button
      onClick={() => setTab(id)}
      data-testid={`tab-${id}`}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition ${
        active ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
      }`}
    >
      {icon} {children}
    </button>
  );
}

function StatCard({ label, value, icon, color, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testId}>
      <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center mb-3`}>{icon}</div>
      <div className="text-3xl font-extrabold tracking-tight text-slate-900">{value}</div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    sent: { cls: "bg-emerald-50 text-emerald-700", label: "Απεστάλη" },
    draft: { cls: "bg-amber-50 text-amber-700", label: "Προεπισκόπηση" },
  };
  const v = map[status] || { cls: "bg-slate-100 text-slate-600", label: status };
  return <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-bold ${v.cls}`}>{v.label}</span>;
}
